import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps, FlexProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text/Text'
import TokenLogo from 'UIKit/TokenLogo'
import { Token, TokenAmount } from 'config/types'
import BaseBalanceSelect from 'layout/Components/BalanceSelect/components/BaseBalanceSelect'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useValidatePlayToken } from 'state/app/hooks'
import { useAuth } from 'state/auth/hooks'
import { useTokenAppBalance } from 'state/profile/hooks'
import styled from 'styled-components'
import TokenAmountFiatDisplay from 'views/Fiat/TokenAmountFiatDisplay'
import { PaymentPageEnums } from 'views/Payment/Menu'
import { usePresentPaymentModal } from 'views/Payment/MobilePayment/hooks'
import GameInfoMessage from './GameInfoMessage'

const TokenAmountLayout: React.FC<{ selectedTokenBalance: TokenAmount } & FlexProps> = ({
  selectedTokenBalance,
  ...props
}) => {
  return (
    <Flex alignItems="center" height="100%" justifyContent="space-between" width="100%" {...props}>
      <Flex alignItems="center">
        <TokenLogo token={selectedTokenBalance.token} minWidth={20} size={20} />
        <Text fontSize="14px" ml="8px" color="textSubtle">
          {selectedTokenBalance.token.name}
        </Text>
      </Flex>
      <Text ml="8px" fontSize="14px" fontWeight={600}>
        <TokenAmountFiatDisplay tokenAmount={selectedTokenBalance} />
      </Text>
    </Flex>
  )
}

interface BalanceSelectSectionProps {
  selectedToken: Token
  setSelectedToken: (token: Token) => void
}

const BalanceSelectSection: React.FC<BoxProps & BalanceSelectSectionProps> = ({
  selectedToken,
  setSelectedToken,
  ...props
}) => {
  const { hasSession } = useAuth()
  const [onPresentModalPayment] = usePresentPaymentModal()
  const { t } = useTranslation()
  const selectedTokenBalance = useTokenAppBalance(selectedToken)
  const { isBlackList } = useValidatePlayToken(selectedToken)

  return (
    <Flex {...props} alignItems="flex-start">
      <Box width="100%">
        <StyledText fontSize={['12px', , , '14px']}>
          <Trans>Play with balance in</Trans>
        </StyledText>

        <StyledBalanceSelect
          selectedToken={selectedToken}
          handleSelectBalance={(amount: TokenAmount) => {
            setSelectedToken(amount.token)
          }}
          maxHeight={220}
          enableSetting={false}
          rowCount={4}
        >
          <TokenAmountLayout selectedTokenBalance={selectedTokenBalance} />
        </StyledBalanceSelect>

        {hasSession && (
          <Box>
            {isBlackList ? (
              <GameInfoMessage
                maxWidth="360px"
                variant="error"
                mt="20px"
                message={t('This token is not supported. Please switch to another currency to continue playing.')}
              />
            ) : (
              selectedTokenBalance.amount.eq(0) && (
                <GameInfoMessage
                  maxWidth="360px"
                  variant="info"
                  mt="20px"
                  message={
                    <Trans
                      i18nKey="playWithLowBalanceMessage"
                      components={{
                        highlight_tag: (
                          <Text
                            color="primary"
                            bold
                            fontSize="inherit"
                            display="inline !important"
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                              onPresentModalPayment({
                                defaultPage: PaymentPageEnums.Deposit,
                                initialToken: selectedToken,
                              })
                            }
                          />
                        ),
                      }}
                    />
                  }
                />
              )
            )}
          </Box>
        )}
      </Box>
    </Flex>
  )
}

const StyledBalanceSelect = styled(BaseBalanceSelect)`
  width: 100% !important;
  backdrop-filter: blur(15px);
  position: relative;
  border-radius: ${({ theme }) => theme.radii.modal};
  padding-left: 8px;
  height: 48px !important;
  margin-top: 8px;
  padding: 14px 20px;
  background: ${({ theme }) => theme.colors.inputBackground};

  ${({ theme: { mediaQueries } }) => mediaQueries.sm} {
    background: ${({ theme }) => theme.colors.card_primary};
  }
`

const StyledText = styled(Text)`
  font-weight: 500;
  color: ${({ theme: { colors } }) => colors.textSubtle};

  ${({ theme }) => theme.mediaQueries.sm} {
    font-size: 12px;
    font-weight: 400;

    color: ${({ theme }) => theme.colors.text};
  }
`
export default BalanceSelectSection
